import React, { useContext } from 'react';
import styled from 'styled-components';
import {
  allMetricKinds,
  isProfitMetricAvailable,
  isReturnsMetricAvailable,
  MetricKind,
} from '@/webapi/models';
import { formatEnum } from '@/utils/types';
import {
  GoalChip,
  GoalsWrapper,
} from '@/features/editor/widgets/shared/inline-variants-layout/goals';
import { Experience, useExperienceApi } from '@/webapi/use-experience-api';
import { breakpoints } from '@/components/responsive';
import { SummaryContext } from '@/features/details/summary/context';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

type Props = { experience: Experience; setExperience: (e: Experience) => void };

export function MoreMetrics({ experience, setExperience }: Props) {
  const { upsertExperience, loading } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  const isCustomMetricsEnbled = useFeatureBit(FeatureBit.CUSTOM_METRICS);
  const { hasMetric, getCustomMetricName } = useContext(SummaryContext);
  const metricKinds = allMetricKinds(experience)
    .filter(allowProfit(experience))
    .filter(allowedCustomMetrics(isCustomMetricsEnbled, hasMetric))
    .filter(allowReturns(experience));

  const scrollToAddedReport = (metricName) => {
    const element = document.querySelector(`div[title="${metricName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: `smooth`, block: `center` });
    }
  };

  return (
    <div>
      <Title>Would like to see more reports?</Title>
      <MetricsList>
        {metricKinds.map((metric) => {
          const onClick = async () => {
            if (loading || metric === experience.primaryGoal) {
              return;
            }
            const cloned = JSON.parse(JSON.stringify(experience)) as Experience;
            cloned.goals.push(metric);
            await setExperience(cloned);
            scrollToAddedReport(metric);
            await upsertExperience(cloned);
          };
          return !selectedGoal(experience, metric) ? (
            <Chip
              focused={selectedGoal(experience, metric)}
              key={metric}
              onClick={onClick}
            >
              {getCustomMetricName(metric) || formatEnum(metric, true)}
            </Chip>
          ) : null;
        })}
      </MetricsList>
    </div>
  );
}

const Title = styled.p`
  user-select: none;
  margin: 0 auto 4rem auto;
  color: #b8c2c9;
  letter-spacing: 0.48px;
  font-family: Inter, serif;
  font-weight: 800;
  font-size: 2.5rem;
  text-align: center;

  ${breakpoints.down(`md`)} {
    font-size: 1.8rem;
  }
`;

const Chip = styled(GoalChip)`
  && {
    background-color: #cfd2d9;
    color: #0c0c0c;
    margin: 1rem;
    font-weight: 700;
    font-size: 1.4rem;
    border: none;
    text-transform: uppercase;
  }
`;

const MetricsList = styled(GoalsWrapper)`
  && {
    margin: 0 auto;
    max-width: 100rem;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
`;

export function selectedGoal(experience: Experience, metric: MetricKind) {
  return experience.goals?.includes?.(metric);
}

export function allowProfit(experience: Experience) {
  return (m) => {
    if (isProfitMetricAvailable(experience?.metricsStartAt)) {
      return true;
    }
    return !(m === MetricKind.PROFIT || m === MetricKind.PROFIT_PER_SESSION);
  };
}

export function allowedCustomMetrics(
  isEnabled: boolean,
  hasMetric: (metric: MetricKind) => boolean,
) {
  return (m) => {
    if (isCustomMetric(m)) {
      return isEnabled && hasMetric(m);
    }
    return true;
  };
}

const isCustomMetric = (m) => m.startsWith(`CUSTOM`);

export function allowReturns(experience: Experience) {
  return (m) => {
    if (isReturnsMetricAvailable(experience?.metricsStartAt)) {
      return true;
    }
    return !(m === MetricKind.RETURNS || m === MetricKind.RETURNS_PER_SESSION);
  };
}
